import { BaseProvider, createTheme } from 'baseui'
import selectraPrimitives from '@/shared/theme/themePrimitives'
import selectraOverrides from '@/shared/theme/themeOverrides'
import SDButton from '@/shared/components/SDComponents/SDButton/SDButton'
import { KIND, SIZE } from 'baseui/button'
import DevToolsSearch from './DevToolsSearch'
import FeedbackList from './FeedbackList'
import FeedbackCircle from './FeedbackCircle'
import useFeedbackList from './useFeedbackList'
import useCsvExport from './useCsvExport'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

const FEEDBACK_VERSIONS = [
  { value: 'legacy', label: 'Legacy' },
  { value: 'new', label: 'New version' }
]

const FeedbackTools = ({ urls }) => {
  const { t } = useTranslation()
  const selectraTheme = createTheme(selectraPrimitives, selectraOverrides)

  const {
    searchInput, setSearchInput,
    filters, setVersion, setFeedbackType, setStartDate, setEndDate,
    feedbackType, feedbackVersion,
    feedbacks, isLoading, error, Paginator
  } = useFeedbackList(urls.feedbacks_list)

  const { exporting, exportError, exportCSV, setExportError } = useCsvExport()

  const handleExport = () => {
    const params = {
      search: filters.search,
      start: filters.start,
      end: filters.end,
      version: feedbackVersion,
      feedbackType,
    }
    const cleanParams = Object.entries(params)
      .filter(([, v]) => v !== '' && v !== undefined && v !== null)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&')
    exportCSV(`${urls.feedbacks_export}?${cleanParams}`)
  }

  const transformFeedbacks = list => {
    if (!list) return []
    return list.map(fb => [
      new Date(fb.created_at).toLocaleString(),
      fb.agent_first_name || '-',
      fb.agent_name || '-',
      <FeedbackCircle key={fb.id} value={fb.feedback} upvoteLabel={t('devTools:feedback.upvote')} downvoteLabel={t('devTools:feedback.downvote')} />,
      fb.comment || '-',
      `${fb.firstname} ${fb.lastname}`,
    ])
  }

  return (
    <BaseProvider theme={selectraTheme}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 16 }}>
        <DevToolsSearch
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('devTools:feedback.search_feedback_placeholder')}
        />
        <select
          value={feedbackVersion}
          onChange={e => setVersion(e.target.value)}
          style={{ height: 36, minWidth: 90, borderRadius: 6, border: '1px solid #ccc', marginRight: 8 }}
        >
          {FEEDBACK_VERSIONS.map(v =>
            <option key={v.value} value={v.value}>{v.label}</option>
          )}
        </select>
        <span style={{ fontWeight: 500 }}>{t('devTools:feedback.date_range_label')}</span>
        <input
          type="date"
          value={filters.start || ''}
          onChange={e => setStartDate(e.target.value)}
          style={{ height: 36, width: 120, borderRadius: 6, border: '1px solid #ccc' }}
        />
        <span>-</span>
        <input
          type="date"
          value={filters.end || ''}
          onChange={e => setEndDate(e.target.value)}
          style={{ height: 36, width: 120, borderRadius: 6, border: '1px solid #ccc' }}
        />
        <select
          value={feedbackType}
          onChange={e => setFeedbackType(e.target.value)}
          style={{ height: 36, borderRadius: 6, border: '1px solid #ccc', minWidth: 85, marginRight: 6 }}
        >
          <option value="all">{t('devTools:feedback.all')}</option>
          <option value="upvote">{t('devTools:feedback.upvote')}</option>
          <option value="downvote">{t('devTools:feedback.downvote')}</option>
        </select>
        <SDButton
          kind={KIND.secondary}
          size={SIZE.compact}
          isLoading={exporting}
          onClick={handleExport}
          style={{ marginLeft: 8, minWidth: '7rem' }}
        >
          {t('devTools:feedback.export_csv')}
        </SDButton>
      </div>
      {exportError && (
        <div style={{ color: 'red', marginTop: 8 }}>
          {exportError}
          <button onClick={() => setExportError(null)} style={{
            background: 'none', border: 'none', color: '#333', marginLeft: '1rem', cursor: 'pointer'
          }}>&times;</button>
        </div>
      )}
      {error && (
        <div style={{ color: 'red', marginBottom: '1rem' }}>
          {typeof error === 'string' ? error : t('devTools:feedback.error_fetching')}
        </div>
      )}
      <FeedbackList items={transformFeedbacks(feedbacks)} isLoading={isLoading} />
      <Paginator />
    </BaseProvider>
  )
}

FeedbackTools.propTypes = {
  urls: PropTypes.object.isRequired,
}

export default FeedbackTools
